/**
 * Garmin Connect API Integration Service
 * Handles Garmin OAuth and activity sync
 */

import { storage } from './storage';
import { fetchWithAuth } from './fetchWithAuth';

export interface GarminConnectionStatus {
  connected: boolean;
  garmin_available?: boolean;  // Garmin light switch — false when awaiting production API approval
  garmin_user_id?: string;
  display_name?: string;
  device_name?: string;  // For brand-compliant attribution per Garmin guidelines
  connected_at?: string;
  profile_image_url?: string;
  last_sync_at?: string;
  // Garmin history re-ask (2026-09-01). Both booleans are served ONLY when
  // connected AND FF_GARMIN_HISTORY_REASK is on server-side — ABSENT otherwise,
  // never false. Readers must treat absent as not eligible (flag-off contract:
  // old and new clients behave pre-sprint). `history_backfill_denied_at` is
  // the denial-episode key the client's seen-guard is keyed to (decision D4),
  // served alongside the booleans; absent on a pre-sprint backend, where
  // garminHistoryReaskLogic.episodeKey falls back to a single 'legacy' episode.
  history_backfill_denied?: boolean;
  history_reask_eligible?: boolean;
  history_backfill_denied_at?: string | null;
}

export interface GarminActivity {
  id: string;
  start_date: string;
  duration_minutes: number;
  distance_km: number;
  sport: string;
  avg_heart_rate?: number;
  max_heart_rate?: number;
  elevation_gain?: number;
  calories?: number;
  name: string;
}

export interface GarminAuthResponse {
  auth_url: string;
  state: string;
  oauth_token: string;
}

import { API_BASE_URL } from '../utils/api'; // Centralized environment-aware API URL

// Authentication helper
const getAuthHeaders = async () => {
  const token = await storage.getItem('jwt_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

class GarminService {
  /**
   * Check if user has connected Garmin account
   */
  async getConnectionStatus(): Promise<GarminConnectionStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/garmin/status`, {
        method: 'GET',
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get connection status');
      }
      
      return await response.json();
    } catch (error) {
      // Use console.warn (not .error) so React Native LogBox doesn't show a red
      // error screen for a routine network-unreachable situation (e.g. simulator
      // with no backend running, or offline).
      console.warn('Failed to get Garmin connection status:', error);
      return { connected: false };
    }
  }

  /**
   * Get Garmin OAuth authorization URL
   */
  async getAuthUrl(): Promise<GarminAuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/garmin/auth-url`, {
        method: 'GET',
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get auth URL');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to get Garmin auth URL:', error);
      throw error;
    }
  }

  /**
   * Initiate Garmin OAuth connection
   * Opens Garmin authorization page in new window
   */
  async connect(): Promise<void> {
    try {
      const authData = await this.getAuthUrl();
      
      // Open Garmin auth page in new window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const authWindow = window.open(
        authData.auth_url,
        'Garmin Authorization',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
      );

      // Listen for callback success
      const checkCallback = setInterval(() => {
        try {
          if (authWindow && authWindow.closed) {
            clearInterval(checkCallback);
            // Refresh connection status after authorization
            window.location.reload();
          }
        } catch (e) {
          // Cross-origin error expected
        }
      }, 500);
    } catch (error) {
      console.error('Failed to initiate Garmin connection:', error);
      throw error;
    }
  }

  /**
   * Check comprehensive sync status (connection, sync, ready)
   */
  async checkSyncStatus(): Promise<{
    connection_status: { ok: boolean; error?: string; message: string };
    sync_status: { ok: boolean; details: any; message: string };
    ready_status: { ok: boolean; message: string };
    overall_status: string;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/garmin/sync-check`, {
        method: 'GET',
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to check sync status');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to check sync status:', error);
      throw error;
    }
  }

  /**
   * Smart sync that only syncs missing activities
   */
  async smartSync(): Promise<{
    status: string;
    message: string;
    existing_activities: number;
    syncing: boolean;
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/garmin/smart-sync`, {
        method: 'POST',
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        // Carry the status: callers must distinguish "not connected" (400 —
        // retrying today adds nothing) from a transient 5xx/network failure
        // (retry on the next Health-tab focus). See healthDailyRefresh.
        const err: Error & { status?: number } = new Error('Failed to start smart sync');
        err.status = response.status;
        throw err;
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to start smart sync:', error);
      throw error;
    }
  }

  /**
   * "No thanks" on the history re-ask dialog. Stamps
   * garmin_tokens.history_reask_dismissed_at — idempotent server-side, durable
   * per connection (a later ACCEPTED backfill clears it). Never throws: a
   * failed dismiss costs at most one more prompt on a fresh install.
   *
   * Goes through fetchWithAuth rather than this file's legacy header helper:
   * the raw-bearer ratchet (tests/gate/test_raw_bearer_ratchet.py) only lets
   * that surface shrink — it counts the helper's NAME anywhere in the file,
   * comments included, so do not spell it here — and fetchWithAuth refuses to
   * fire with no credential where the legacy helper would POST unauthenticated.
   */
  async dismissHistoryReask(): Promise<boolean> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/garmin/history-reask/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        // 410 = FF_GARMIN_HISTORY_REASK was flipped off mid-session (the modal
        // cannot have fired without the status field the same flag gates).
        console.warn('Garmin history re-ask dismiss failed:', response.status);
        return false;
      }

      const body = await response.json().catch(() => ({}));
      return body?.dismissed !== false;
    } catch (error) {
      console.warn('Garmin history re-ask dismiss failed:', error);
      return false;
    }
  }

  /**
   * Disconnect Garmin account
   */
  async disconnect(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/garmin/disconnect`, {
        method: 'POST',
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Failed to disconnect Garmin:', error);
      throw error;
    }
  }

  /**
   * Get recent Garmin activities
   */
  async getRecentActivities(limit: number = 10): Promise<GarminActivity[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/garmin/activities?limit=${limit}`, {
        method: 'GET',
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get activities');
      }
      
      const data = await response.json();
      return data.activities || [];
    } catch (error) {
      // Use console.warn (not .error) to avoid red screen in React Native dev mode
      // when backend is unreachable -- this is a passive data fetch, not user-initiated.
      console.warn('Failed to get Garmin activities:', error);
      return [];
    }
  }
}

export default new GarminService();



